/**
 * Which model this reader last had open.
 *
 * WHY THIS EXISTS
 * "Chat with Kev" on /app used to open whichever model sat first in the
 * catalogue that the reader could access. The catalogue is ordered for
 * BROWSING, not for resumption, and its first entry is `nba-fantasy` — so a
 * button labelled "Chat with Kev" reliably opened FRANCO, and did so even for a
 * reader whose only interest was football. Catalogue order is a display
 * decision; it was never meant to carry the meaning "this is your model".
 *
 * WHY localStorage AND NOT THE SERVER
 * This is a convenience, not an entitlement. Nothing here decides what a reader
 * may open — `canAccess` does, and the resolved id is checked against it on
 * every read (see `pickChatModel`). A stale or hand-edited value can therefore
 * only ever choose between models the reader could already reach, which makes
 * this safe to keep client-side and not worth a round trip.
 *
 * localStorage rather than sessionStorage ON PURPOSE, unlike `lazyRoute`'s
 * reload guard: the point is to survive the tab closing. A "last used" that
 * forgets overnight is just catalogue order again with extra steps.
 */

const KEY = "kev.lastModel";

/** Record that the reader opened this model. Never throws. */
export function rememberModel(modelId: string): void {
  if (!modelId) return;
  try {
    localStorage.setItem(KEY, modelId);
  } catch {
    /* Private mode, quota, disabled storage. Losing the preference is a
       downgrade to the default, which is a working state — it must never be
       allowed to break opening a chat. */
  }
}

/** The last model opened, or null. Never throws. */
export function lastModel(): string | null {
  try {
    return localStorage.getItem(KEY) || null;
  } catch {
    return null;
  }
}

/** Forget the preference. Used on sign-out so the next reader is not routed
 *  into the previous one's model. */
export function forgetModel(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* see rememberModel */
  }
}
